// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConnectionIssuesLinks} from '@app/features/app/components/ConnectionIssuesLinks';
import {NativeDragRegion} from '@app/features/app/components/layout/NativeDragRegion';
import styles from '@app/features/app/components/layout/SplashScreen.module.css';
import Initialization from '@app/features/app/state/Initialization';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import {FluxerIcon} from '@app/features/ui/components/icons/FluxerIcon';
import {getReducedMotionProps} from '@app/features/ui/utils/ReducedMotionAnimation';
import StatusPage from '@app/features/user/state/StatusPage';
import {type SplashQuote, useSplashQuotes} from '@app/media/data/SplashQuotes';
import {ExternalUrls} from '@app/vendor/constants/ExternalUrls';
import {Trans} from '@lingui/react/macro';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

const PROBLEMS_DELAY = 10000;

const STATUS_PAGE_DISPLAY_DELAY = 5000;

type SplashScreenMode = 'live' | 'outage';
export const SplashScreen = observer(() => {
	const shouldBypass = DeveloperOptions.bypassSplashScreen;
	const connected = GatewayConnection.isConnected;
	const isInitialized = Initialization.canNavigateToProtectedRoutes;
	const isReady = connected && isInitialized;
	if (shouldBypass) return null;
	return <AnimatePresence initial={false}>{!isReady && <SplashScreenContent mode="live" />}</AnimatePresence>;
});

export const OutageSplashScreen = observer(() => <SplashScreenContent mode="outage" />);
const SPLASH_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.35},
};
const CONNECTION_ISSUES_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.4},
};
const INCIDENT_CTA_MOTION = {
	initial: {opacity: 0},
	animate: {opacity: 1},
	exit: {opacity: 0},
	transition: {duration: 0.2},
};
interface SplashScreenContentProps {
	mode: SplashScreenMode;
}
function pickRandomQuote(quotes: ReadonlyArray<SplashQuote>): SplashQuote {
	return quotes[Math.floor(Math.random() * quotes.length)];
}
const SplashScreenContent = observer(({mode}: SplashScreenContentProps) => {
	const isOutageMode = mode === 'outage';
	const splashMotion = getReducedMotionProps(SPLASH_MOTION, Accessibility.useReducedMotion);
	const connectionIssuesMotion = getReducedMotionProps(CONNECTION_ISSUES_MOTION, Accessibility.useReducedMotion);
	const incidentCtaMotion = getReducedMotionProps(INCIDENT_CTA_MOTION, Accessibility.useReducedMotion);
	const connected = isOutageMode ? false : GatewayConnection.isConnected;
	const isInitialized = isOutageMode ? false : Initialization.canNavigateToProtectedRoutes;
	const isReady = connected && isInitialized;
	const splashQuotes = useSplashQuotes();
	const incident = StatusPage.incident;
	const quoteRef = useRef<SplashQuote | null>(null);
	if (quoteRef.current == null) {
		quoteRef.current = pickRandomQuote(splashQuotes);
	}
	const quote = quoteRef.current;
	const [showStatusData, setShowStatusData] = useState(isOutageMode);
	const [showProblems, setShowProblems] = useState(isOutageMode);
	const problemsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearProblemsTimer = useCallback(() => {
		if (problemsTimerRef.current != null) {
			clearTimeout(problemsTimerRef.current);
			problemsTimerRef.current = null;
		}
	}, []);
	useEffect(() => {
		if (isOutageMode) {
			setShowStatusData(true);
			return;
		}
		const timer = setTimeout(() => setShowStatusData(true), STATUS_PAGE_DISPLAY_DELAY);
		return () => clearTimeout(timer);
	}, [isOutageMode]);
	useEffect(() => {
		if (isOutageMode) {
			clearProblemsTimer();
			setShowProblems(true);
			void StatusPage.checkIncidents();
			return;
		}
		if (isReady) {
			clearProblemsTimer();
			setShowProblems(false);
			return;
		}
		problemsTimerRef.current = setTimeout(() => {
			setShowProblems(true);
			void StatusPage.checkIncidents();
		}, PROBLEMS_DELAY);
		return clearProblemsTimer;
	}, [clearProblemsTimer, isOutageMode, isReady]);
	const liveIncident = showStatusData ? incident : null;
	const frozenDisplayRef = useRef<{incident: typeof liveIncident; text: string} | null>(null);
	if (!isReady) {
		frozenDisplayRef.current = {
			incident: liveIncident,
			text: liveIncident ? liveIncident.name : quote.text,
		};
	}
	const visibleIncident = frozenDisplayRef.current?.incident ?? liveIncident;
	const displayText = frozenDisplayRef.current?.text ?? quote.text;
	const incidentUrl = visibleIncident?.url ?? ExternalUrls.SERVICE_STATUS;
	if (isOutageMode) {
		return (
			<div className={styles.splashOverlay}>
				<NativeDragRegion className={styles.topDragRegion} />
				<div className={styles.splashContent}>
					<div className={styles.iconWrapper}>
						<div className={`${styles.iconPulse} ${styles.iconPulseStatic}`} />
						<FluxerIcon className={styles.icon} />
					</div>
					<div className={styles.quoteContainer}>
						{visibleIncident != null ? (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
								{displayText}
							</a>
						) : (
							<p className={styles.quote}>
								<Trans>Connection lost</Trans>
							</p>
						)}
						{visibleIncident != null && (
							<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.incidentCta}>
								<Trans>View on status page</Trans>
							</a>
						)}
					</div>
				</div>
				{showProblems && !isReady && visibleIncident == null && (
					<div className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</div>
				)}
			</div>
		);
	}
	return (
		<motion.div {...splashMotion} className={styles.splashOverlay}>
			<NativeDragRegion className={styles.topDragRegion} />
			<div className={styles.splashContent}>
				<div className={styles.iconWrapper}>
					<div className={styles.iconPulse} />
					<FluxerIcon className={styles.icon} />
				</div>
				<div className={styles.quoteContainer}>
					{visibleIncident != null ? (
						<a href={incidentUrl} target="_blank" rel="noopener noreferrer" className={styles.quoteLink}>
							{displayText}
						</a>
					) : (
						<>
							<p className={styles.quote}>{displayText}</p>
							<p className={styles.quoteSource}>{quote.source}</p>
						</>
					)}
					<AnimatePresence>
						{visibleIncident != null && (
							<motion.a
								{...incidentCtaMotion}
								href={incidentUrl}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.incidentCta}
							>
								<Trans>View on status page</Trans>
							</motion.a>
						)}
					</AnimatePresence>
				</div>
			</div>
			<AnimatePresence>
				{showProblems && !isReady && visibleIncident == null && (
					<motion.div {...connectionIssuesMotion} className={styles.connectionIssuesOverlay}>
						<ConnectionIssuesLinks incident={incident} />
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
});
